import { FormEvent, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faFloppyDisk,
  faLink,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getUser, postUser } from "../../helper/http";
import styles from "./AppDownloadsSettingsPage.module.css";

interface AppDownloadsSettingsResponse {
  result: boolean;
  url: string;
}

function validationError(value: string) {
  const text = value.trim();
  if (!text) return "Enter the Web version URL.";
  if (text.length > 100) return "The URL must not exceed 100 characters.";
  try {
    const url = new URL(text);
    if (!["http:", "https:"].includes(url.protocol)) {
      return "Only HTTP and HTTPS links are supported.";
    }
    if (url.username || url.password) {
      return "The link must not contain credentials.";
    }
  } catch {
    return "Enter a valid absolute URL.";
  }
  return "";
}

export default function AppDownloadsSettingsPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const clientValidationError = useMemo(() => validationError(url), [url]);
  const dirty = url.trim() !== savedUrl;

  const errorMessage = (err: any, fallback: string) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const load = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = (await getUser(
        "settings/admin/app-downloads",
      )) as AppDownloadsSettingsResponse;
      const nextUrl = response?.url?.trim() || "";
      setUrl(nextUrl);
      setSavedUrl(nextUrl);
    } catch (err: any) {
      setError(errorMessage(err, "Failed to load the Web version URL."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving || loading || clientValidationError) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = (await postUser("settings/admin/app-downloads", {
        url: url.trim(),
      })) as AppDownloadsSettingsResponse;
      const nextUrl = response?.url?.trim() || url.trim();
      setUrl(nextUrl);
      setSavedUrl(nextUrl);
      setSuccess("Web version URL saved. Smoke DEV will use it immediately.");
    } catch (err: any) {
      setError(errorMessage(err, "Failed to save the Web version URL."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => navigate("/delivery-operations")}
            title="Back to Delivery operations"
            aria-label="Back to Delivery operations"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1>Web version address</h1>
            <span>Link shown in Smoke DEV settings as “Web version”.</span>
          </div>
        </div>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={load}
          disabled={loading || saving}
        >
          <FontAwesomeIcon icon={faRotate} />
          <span>{loading ? "Loading" : "Reload"}</span>
        </button>
      </header>

      <section className={styles.card}>
        <div className={styles.cardTitle}>
          <FontAwesomeIcon icon={faLink} />
          <div>
            <h2>Web version URL</h2>
            <p>
              Changing this value updates the Web version button in the DEV
              application. Native downloads continue to come from the update
              API.
            </p>
          </div>
        </div>

        <form onSubmit={save} className={styles.form}>
          <label className={styles.field}>
            <span>Address</span>
            <input
              type="url"
              inputMode="url"
              value={url}
              maxLength={100}
              placeholder="https://smoke.team/"
              onChange={(event) => {
                setUrl(event.target.value);
                setError("");
                setSuccess("");
              }}
              disabled={loading || saving}
              aria-invalid={Boolean(url && clientValidationError)}
            />
          </label>

          {url && clientValidationError && (
            <div className={styles.validation}>{clientValidationError}</div>
          )}
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <div className={styles.actions}>
            <button
              type="submit"
              disabled={
                loading || saving || !dirty || Boolean(clientValidationError)
              }
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
              <span>{saving ? "Saving" : "Save address"}</span>
            </button>
            {savedUrl && !validationError(savedUrl) && (
              <a href={savedUrl} target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                <span>Open current link</span>
              </a>
            )}
          </div>
        </form>
      </section>
    </main>
  );
}
